/**
 * Sdílené ExcelJS styly pro AOA exporty (tučná hlavička, rámečky, barevný status).
 * Materiálový souhrn i ostatní tabulkové exporty používají stejné API.
 */

import type * as ExcelJS from 'exceljs';
import {
  getObjectStatusColor,
  normalizePolygonPlanStatus,
  POLYGON_PLAN_STATUS_TO_FILTER_SLUG,
} from '@core/shared-bridge';
import type { PolygonPlanStateKey } from './polygon-project-tracking.util';

/** Výplň hlavičky — neutrální šedá, čitelná na světlém i tmavém motivu Excelu. */
export const EXCEL_EXPORT_HEADER_FILL_ARGB = 'FFD1D5DB';
export const EXCEL_EXPORT_HEADER_FONT_ARGB = 'FF111827';

/** Polygon stav složky — světlé výplně odpovídající UI badge. */
export const POLYGON_PLAN_STATE_EXCEL_FILLS: Record<
  Exclude<PolygonPlanStateKey, ''>,
  { fillArgb: string; fontArgb: string }
> = {
  nezarazene: { fillArgb: 'FFF3F4F6', fontArgb: 'FF4B5563' },
  projekce: { fillArgb: 'FFDBEAFE', fontArgb: 'FF1D4ED8' },
  priprava_staveb: { fillArgb: 'FFFEF3C7', fontArgb: 'FFB45309' },
  stavba: { fillArgb: 'FFD1FAE5', fontArgb: 'FF047857' },
  archiv: { fillArgb: 'FFCCFBF1', fontArgb: 'FF0F766E' },
};

/** Stavební deník — stav řádku. */
export const DIARY_STATUS_EXCEL_FILLS: Record<string, { fillArgb: string; fontArgb: string }> = {
  Potvrzeno: { fillArgb: 'FFD1FAE5', fontArgb: 'FF047857' },
  Nezačato: { fillArgb: 'FFF3F4F6', fontArgb: 'FF4B5563' },
  Rozpracováno: { fillArgb: 'FFFEF3C7', fontArgb: 'FFB45309' },
};

/** Boolean Ano/Ne — stejný zelený / červený tón jako aktivní / storno stav složky. */
export const BOOL_ANO_NE_EXCEL_FILLS: Record<'Ano' | 'Ne', { fillArgb: string; fontArgb: string }> = {
  Ano: { fillArgb: 'FFD1FAE5', fontArgb: 'FF047857' },
  Ne: { fillArgb: 'FFFEE2E2', fontArgb: 'FFB91C1C' },
};

const THIN: Partial<ExcelJS.Border> = { style: 'thin' };
const THICK: Partial<ExcelJS.Border> = { style: 'thick' };

export function hexToExcelArgb(hex: string): string | null {
  const raw = hex.trim().replace(/^#/, '');
  if (/^[0-9a-fA-F]{6}$/.test(raw)) return `FF${raw.toUpperCase()}`;
  if (/^[0-9a-fA-F]{8}$/.test(raw)) return raw.toUpperCase();
  if (/^[0-9a-fA-F]{3}$/.test(raw)) {
    const r = raw[0]!;
    const g = raw[1]!;
    const b = raw[2]!;
    return `FF${(r + r + g + g + b + b).toUpperCase()}`;
  }
  return null;
}

/** Promíchá hex s bílou (Excel fill nepodporuje spolehlivě alfa). */
export function lightenHexForExcelFill(hex: string, mixWhite = 0.72): string | null {
  const argb = hexToExcelArgb(hex);
  if (!argb || argb.length !== 8) return null;
  const r = Number.parseInt(argb.slice(2, 4), 16);
  const g = Number.parseInt(argb.slice(4, 6), 16);
  const b = Number.parseInt(argb.slice(6, 8), 16);
  if (![r, g, b].every((n) => Number.isFinite(n))) return null;
  const t = Math.min(1, Math.max(0, mixWhite));
  const lr = Math.round(r + (255 - r) * t);
  const lg = Math.round(g + (255 - g) * t);
  const lb = Math.round(b + (255 - b) * t);
  return `FF${[lr, lg, lb].map((n) => n.toString(16).padStart(2, '0')).join('').toUpperCase()}`;
}

/** Tenké vnitřní + silný vnější rámeček obdélníku (1-based indexy). */
export function applyExcelTableBorders(
  ws: ExcelJS.Worksheet,
  topRow: number,
  bottomRow: number,
  leftCol: number,
  rightCol: number,
): void {
  for (let row = topRow; row <= bottomRow; row++) {
    for (let col = leftCol; col <= rightCol; col++) {
      const cell = ws.getCell(row, col);
      cell.border = {
        top: row === topRow ? THICK : THIN,
        left: col === leftCol ? THICK : THIN,
        bottom: row === bottomRow ? THICK : THIN,
        right: col === rightCol ? THICK : THIN,
      };
    }
  }
}

export function styleExcelHeaderRow(
  ws: ExcelJS.Worksheet,
  headerRow: number,
  colCount: number,
  options?: { fillArgb?: string; fontArgb?: string },
): void {
  const fillArgb = options?.fillArgb ?? EXCEL_EXPORT_HEADER_FILL_ARGB;
  const fontArgb = options?.fontArgb ?? EXCEL_EXPORT_HEADER_FONT_ARGB;
  for (let col = 1; col <= colCount; col++) {
    const cell = ws.getCell(headerRow, col);
    cell.font = { ...(cell.font ?? {}), bold: true, color: { argb: fontArgb } };
    cell.fill = { type: 'pattern', pattern: 'solid', fgColor: { argb: fillArgb } };
    cell.alignment = { ...(cell.alignment ?? {}), vertical: 'middle', wrapText: true };
  }
}

export function applyExcelStatusCellFill(
  cell: ExcelJS.Cell,
  fillHexOrArgb: string,
  fontArgb?: string,
): void {
  const fillArgb =
    fillHexOrArgb.length === 8 && !fillHexOrArgb.startsWith('#')
      ? fillHexOrArgb.toUpperCase()
      : hexToExcelArgb(fillHexOrArgb) ?? lightenHexForExcelFill(fillHexOrArgb);
  if (!fillArgb) return;
  cell.fill = { type: 'pattern', pattern: 'solid', fgColor: { argb: fillArgb } };
  if (fontArgb) {
    cell.font = { ...(cell.font ?? {}), color: { argb: fontArgb }, bold: true };
  }
}

/** 1-based index sloupce podle textu hlavičky (case-insensitive, trim). */
export function findAoAHeaderColumnIndex(headers: readonly unknown[], ...candidates: string[]): number | null {
  const wanted = candidates.map((c) => c.trim().toLocaleLowerCase('cs-CZ'));
  for (let i = 0; i < headers.length; i++) {
    const h = String(headers[i] ?? '')
      .trim()
      .toLocaleLowerCase('cs-CZ');
    if (wanted.includes(h)) return i + 1;
  }
  return null;
}

export function estimateExcelColumnWidths(
  aoa: readonly (readonly (string | number | boolean | null | undefined)[])[],
  options?: { min?: number; max?: number; pad?: number },
): number[] {
  const min = options?.min ?? 8;
  const max = options?.max ?? 42;
  const pad = options?.pad ?? 2;
  const colCount = aoa.reduce((m, r) => Math.max(m, r.length), 0);
  const widths = Array.from({ length: colCount }, () => min);
  for (const row of aoa) {
    for (let i = 0; i < row.length; i++) {
      const text = row[i] == null || row[i] === '' ? '' : String(row[i]);
      const len = Math.min(max, Math.max(min, text.length + pad));
      if (len > widths[i]!) widths[i] = len;
    }
  }
  return widths;
}

export function polygonPlanStateKeyFromLabel(label: string): PolygonPlanStateKey {
  const trimmed = label.trim();
  const normalized = trimmed.toLocaleLowerCase('cs-CZ');
  if (
    !normalized
    || normalized === 'neurčeno'
    || normalized === 'neurcen'
    || normalized === 'nezařazené (prázdný)'
    || normalized === 'nepřiřazeno (prázdný)'
  ) {
    return '';
  }
  const status = normalizePolygonPlanStatus(trimmed);
  if (!status) return '';
  return POLYGON_PLAN_STATUS_TO_FILTER_SLUG[status];
}

export function resolvePolygonPlanStateExcelStyle(
  label: string,
): { fillArgb: string; fontArgb: string } | null {
  const key = polygonPlanStateKeyFromLabel(label);
  if (!key) return null;
  return POLYGON_PLAN_STATE_EXCEL_FILLS[key];
}

/** Světlá výplň z workflow / polygon status barvy (`getObjectStatusColor`). */
export function resolveObjectStatusExcelFillArgb(statusCode: string | null | undefined): string | null {
  const code = (statusCode ?? '').trim();
  if (!code) return null;
  return lightenHexForExcelFill(getObjectStatusColor(code));
}

export function resolveDiaryStatusExcelStyle(
  label: string,
): { fillArgb: string; fontArgb: string } | null {
  return DIARY_STATUS_EXCEL_FILLS[label.trim()] ?? null;
}

export function resolveAnoNeExcelStyle(
  label: string,
): { fillArgb: string; fontArgb: string } | null {
  const normalized = label.trim().toLocaleLowerCase('cs-CZ');
  if (normalized === 'ano') return BOOL_ANO_NE_EXCEL_FILLS.Ano;
  if (normalized === 'ne') return BOOL_ANO_NE_EXCEL_FILLS.Ne;
  return null;
}

/**
 * Pro každý sloupec AoA, který v datech obsahuje alespoň jedno Ano/Ne, sestaví statusColumn styly.
 * Hlavička (řádek 0) se přeskočí.
 */
export function collectAnoNeStatusColumnsFromAoA(
  aoa: readonly (readonly unknown[])[],
): AoAExportStatusColumnStyle[] {
  if (aoa.length < 2) return [];
  const colCount = aoa.reduce((m, r) => Math.max(m, r.length), 0);
  const out: AoAExportStatusColumnStyle[] = [];
  for (let colIdx = 0; colIdx < colCount; colIdx++) {
    const fills: Array<string | null> = [];
    const fonts: Array<string | null> = [];
    let hasBool = false;
    for (let rowIdx = 1; rowIdx < aoa.length; rowIdx++) {
      const style = resolveAnoNeExcelStyle(String(aoa[rowIdx]?.[colIdx] ?? ''));
      if (style) hasBool = true;
      fills.push(style?.fillArgb ?? null);
      fonts.push(style?.fontArgb ?? null);
    }
    if (hasBool) out.push({ col: colIdx + 1, fills, fonts });
  }
  return out;
}

/**
 * BUDOVA check columns: green „Ano“, red exclusion fill (empty cell), else no fill.
 * `exclusionFlags[dataRow][checkIdx]` aligns with `checkColumnIndexes` (1-based cols).
 */
export function collectBudovaPripojeniCheckStatusColumns(
  aoa: readonly (readonly unknown[])[],
  checkColumnIndexes: readonly number[],
  exclusionFlags: readonly (readonly boolean[])[],
  exclusionFillArgb: string,
): AoAExportStatusColumnStyle[] {
  if (aoa.length < 2 || checkColumnIndexes.length === 0) return [];
  const dataRowCount = aoa.length - 1;
  const out: AoAExportStatusColumnStyle[] = [];
  for (let ci = 0; ci < checkColumnIndexes.length; ci++) {
    const col = checkColumnIndexes[ci]!;
    const fills: Array<string | null> = [];
    const fonts: Array<string | null> = [];
    let hasStyle = false;
    for (let ri = 0; ri < dataRowCount; ri++) {
      if (exclusionFlags[ri]?.[ci] === true) {
        fills.push(exclusionFillArgb);
        fonts.push(null);
        hasStyle = true;
        continue;
      }
      const value = String(aoa[ri + 1]?.[col - 1] ?? '');
      const anoNe = resolveAnoNeExcelStyle(value);
      if (anoNe) hasStyle = true;
      fills.push(anoNe?.fillArgb ?? null);
      fonts.push(anoNe?.fontArgb ?? null);
    }
    if (hasStyle) out.push({ col, fills, fonts });
  }
  return out;
}

export interface AoAExportStatusColumnStyle {
  /** 1-based index sloupce. */
  col: number;
  /**
   * Výplně pro datové řádky (bez hlavičky), index 0 = první datový řádek.
   * Hex `#RRGGBB` / `RRGGBB` / ARGB `AARRGGBB`, nebo null = bez výplně.
   */
  fills: Array<string | null | undefined>;
  /** Volitelné barvy fontu (stejná délka jako fills). */
  fonts?: Array<string | null | undefined>;
}

export interface AoAExportStyleOptions {
  /** 1-based řádek hlavičky (výchozí 1). */
  headerRow?: number;
  /** Počet sloupců; výchozí z délky řádku hlavičky. */
  colCount?: number;
  headerFillArgb?: string;
  /** 1-based řádky, které mají být tučné (např. CELKEM / Součet). */
  boldRows?: number[];
  statusColumns?: AoAExportStatusColumnStyle[];
  /** Šířky sloupců (characters); pokud chybí, odhadne se z hodnot. */
  columnWidths?: Array<number | undefined>;
  /** Odhadnout šířky z aktuálních hodnot listu (pokud columnWidths není). */
  autoColumnWidths?: boolean;
}

function cellDisplayLength(value: ExcelJS.CellValue): number {
  if (value == null || value === '') return 0;
  if (typeof value === 'string' || typeof value === 'number' || typeof value === 'boolean') {
    return String(value).length;
  }
  if (typeof value === 'object' && 'text' in value && typeof (value as { text?: unknown }).text === 'string') {
    return (value as { text: string }).text.length;
  }
  if (typeof value === 'object' && 'result' in value) {
    const result = (value as { result?: unknown }).result;
    return result == null ? 0 : String(result).length;
  }
  return 0;
}

/**
 * Obecný styl AOA listu: tučná zvýrazněná hlavička, rámečky, volitelně barevný status a šířky.
 */
export function styleAoAExportSheet(
  ws: ExcelJS.Worksheet,
  rowCount: number,
  options?: AoAExportStyleOptions,
): void {
  const headerRow = options?.headerRow ?? 1;
  if (rowCount < headerRow) return;

  const headerCells = ws.getRow(headerRow).values;
  const inferredCols =
    Array.isArray(headerCells) ? Math.max(0, headerCells.length - 1) : ws.columnCount;
  const colCount = Math.max(1, options?.colCount ?? (inferredCols || ws.columnCount || 1));

  styleExcelHeaderRow(ws, headerRow, colCount, {
    fillArgb: options?.headerFillArgb,
  });

  for (const rowIdx of options?.boldRows ?? []) {
    if (rowIdx < 1 || rowIdx > rowCount) continue;
    for (let col = 1; col <= colCount; col++) {
      const cell = ws.getCell(rowIdx, col);
      cell.font = { ...(cell.font ?? {}), bold: true };
    }
  }

  for (const statusCol of options?.statusColumns ?? []) {
    const dataStart = headerRow + 1;
    for (let i = 0; i < statusCol.fills.length; i++) {
      const fill = statusCol.fills[i];
      if (!fill) continue;
      const row = dataStart + i;
      if (row > rowCount) break;
      const font = statusCol.fonts?.[i] ?? undefined;
      applyExcelStatusCellFill(ws.getCell(row, statusCol.col), fill, font ?? undefined);
    }
  }

  applyExcelTableBorders(ws, headerRow, rowCount, 1, colCount);

  if (options?.columnWidths && options.columnWidths.length > 0) {
    ws.columns = options.columnWidths.map((w) => ({ width: w ?? 12 }));
  } else if (options?.autoColumnWidths !== false) {
    const widths: number[] = [];
    for (let col = 1; col <= colCount; col++) {
      let maxLen = 8;
      for (let row = headerRow; row <= rowCount; row++) {
        maxLen = Math.max(maxLen, Math.min(42, cellDisplayLength(ws.getCell(row, col).value) + 2));
      }
      widths.push(maxLen);
    }
    ws.columns = widths.map((width) => ({ width }));
  }
}

/** Stylovač pro jednoduchý AOA list (hlavička = řádek 1, bez status sloupců). */
export function createBasicAoAStyleSheet(
  extras?: Omit<AoAExportStyleOptions, 'headerRow'>,
): (ws: ExcelJS.Worksheet, rowCount: number) => void {
  return (ws, rowCount) => styleAoAExportSheet(ws, rowCount, extras);
}
